import React, { useRef, useEffect, useContext, useState } from "react";
import Typed from "typed.js";
import { motion } from "framer-motion/dist/framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faMapMarkerAlt,
	faUniversity,
	faBriefcase,
} from "@fortawesome/free-solid-svg-icons";
import { headingVariant, containerVariant, itemVariant } from "./PageVariants";
import { NavContext } from "./App";
import { InView } from "react-intersection-observer";

export default function Qualifications({ pageRef }) {
	const typedElementForSub = useRef(null);
	const typedElementForHeading = useRef(null);
	const setSelectedNav = useContext(NavContext);
	const [isInView, setIsInView] = useState(false);

	const qualifications = [
		{
			type: "EDUCATION",
			post: "Bachelors of Technology",
			at: "Jawaharlal Nehru Technological University",
			location: "Hyderabad, India",
			year: "2011",
		},
		{
			type: "EDUCATION",
			post: "Masters in Computer Science",
			at: "University of Bridgeport",
			location: "Bridgeport, CT, USA",
			year: "2015",
		},
		{
			type: "JOB",
			post: "Graduate Teaching Assistant",
			at: "University of Bridgeport",
			location: "Bridgeport, CT, USA",
			year: "2016",
		},
		{
			type: "JOB",
			post: "Web Developer",
			at: "Digitech Computer Inc",
			location: "Chappaqua, NY, USA",
			year: "2017",
		},
		{
			type: "JOB",
			post: "Mid-level Web Developer",
			at: "Stephen Einstein & Associates, P.C",
			location: "Manhattan, NY, USA",
			year: "2020",
		},
		{
			type: "JOB",
			post: "Senior Full Stack Developer",
			at: "Tromberg, Morris & Poulin, PLLC",
			location: "Manhattan, NY, USA",
			year: "2021",
		},
		{
			type: "JOB",
			post: "Senior Full Stack Software Engineer",
			at: "MetLife Group Inc.",
			location: "Cary, NC, USA",
			year: "Current",
		},
	];

	useEffect(() => {
		let typedSub = new Typed(typedElementForSub.current, {
			strings: ["BELOW ARE MY"],
			startDelay: 0,
			typeSpeed: 40,
			showCursor: false,
		});

		let typedHeading = new Typed(typedElementForHeading.current, {
			strings: ["QUALIFICATIONS"],
			startDelay: 800,
			typeSpeed: 80,
			showCursor: false,
		});

		return () => {
			typedSub.destroy();
			typedHeading.destroy();
		};
	}, [isInView]);

	const viewChange = (inView) => {
		setIsInView(inView);
		if (inView) {
			setSelectedNav({ Heading: "WORKS" });
		}
	};

	return (
		<InView threshold={0.1} onChange={(inView, entry) => viewChange(inView)}>
			<div
				ref={pageRef}
				className="z-0 w-full md:w-10/12 m-auto grid grid-cols-1 justify-start items-start"
			>
				<motion.h4
					variants={headingVariant}
					initial="hide"
					animate={isInView ? "show" : "hide"}
					exit="hide"
					className="text-3xl text-green-600 border-l-8 border-green-600 pl-4 pt-6 h-24 tracking-wider"
				>
					<span
						className="text-gray-600 dark:text-gray-400 text-sm"
						ref={typedElementForSub}
					></span>
					<br />
					<span className="text-green-600" ref={typedElementForHeading}></span>
				</motion.h4>
				<motion.div
					variants={containerVariant}
					initial="hide"
					animate="show"
					className="text-gray-500 p-1 md:p-4 lg:p-10 my-8 bg-white rounded-3xl text-xs dark:text-gray-200 dark:bg-gray-600"
				>
					<div className="relative rounded-xl px-2">
						<div className="absolute rounded-full bg-gray-500 h-full w-0.5 top-1/2 left-14 md:inset-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
						{qualifications &&
							qualifications.map((singleItem, idx) => (
								<motion.div
									variants={itemVariant}
									key={singleItem.post}
									className={`relative rounded-lg m-0 ${
										idx % 2 === 0 ? "md:mr-50" : "md:ml-50"
									} my-2`}
								>
									<div
										className={`absolute bg-gray-500 h-3 w-4 top-1/2 left-12 ${
											idx % 2 === 0
												? "right-auto md:-right-4 md:left-auto"
												: "md:left-0"
										} md:left-auto transform -translate-x-1/2 -translate-y-1/2`}
									></div>
									<div
										className={`space-y-1 p-4 ml-24 ${
											idx % 2 === 0 ? "md:mr-16 md:ml-0" : "md:ml-16"
										} rounded-xl shadow-xl border border-gray-500 text-green-600 dark:bg-gray-800`}
									>
										<h3 className="text-base md:text-base lg:text-lg font-semibold capitalize">
											{singleItem.post}
										</h3>
										<p className="text-sm text-gray-500 dark:text-gray-200">
											<FontAwesomeIcon
												icon={
													singleItem.type === "JOB" ? faBriefcase : faUniversity
												}
												className="mr-1"
											/>
											{singleItem.at}
										</p>
										<p className="text-sm text-gray-500 dark:text-gray-200">
											<FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1" />
											{singleItem.location}
										</p>
									</div>
									<div
										className={`absolute top-1/2 left-12 ${
											idx % 2 === 0 ? "md:right-2 md:left-auto" : "md:left-2"
										} h-0.5 w-12 md:w-14 bg-gray-500 border border-gray-500`}
									></div>
									<p
										className={`absolute top-1/2 left-4 ${
											idx % 2 === 0
												? "md:-right-20 md:left-auto"
												: "md:-left-10"
										} font-bold text-green-600 transform -translate-x-1/2 -translate-y-1/2`}
									>
										{singleItem.year}
									</p>
								</motion.div>
							))}
					</div>
				</motion.div>
			</div>
		</InView>
	);
}
